import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useTankMonitors } from '@api/useTankMonitors'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { BBDTag, GraviGrid, Texto } from '@gravitate-js/excalibrr'
import { GetDuration } from '@utils/general'
import { Tooltip } from 'antd'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tank-monitors'

export function TankMonitor() {
  const [filters, setFilters] = useState()

  const { useTankMonitorOverviewQuery } = useTankMonitors()
  const { data: tankMonitors, status } = useTankMonitorOverviewQuery(filters)
  const { updateSetting } = useUserSettings()

  const columnDefs = useMemo(() => getColumnDefs(), [])
  const getRowId = useCallback((row) => row.data?.identity, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Tank Monitors',
        setFilters,
        filters,
        serverParams: tankMonitors?.params,
      }}
      loading={status === 'pending'}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      storageKey={STORAGE_KEY}
      rowData={tankMonitors?.rows}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = () => [
  {
    headerName: 'Status',
    field: 'status',
    width: 60,
    flex: 0,
    comparator: (a, b, nodeA, nodeB) => {
      const compareSettings = {
        numeric: true,
        sensitivity: 'base',
      }

      return nodeA?.data?.store_number.localeCompare(nodeB?.data?.store_number, undefined, compareSettings)
    },
    valueGetter: ({ data }) => {
      const duration = GetDuration(data.updated)
      const seconds = duration.get('minutes') * 60

      if (seconds > data.sample_rate * 2) {
        return 'Expired'
      }
      if (data.status.toLowerCase() === 'fail') {
        return 'Failure'
      }
      return 'Stable'
    },
    cellRenderer: ({ value }) => {
      if (value !== 'Stable') {
        return (
          <Tooltip title={`Last Reading ${value === 'Expired' ? 'Expired' : 'Failed'}`}>
            <Texto appearance='error'>
              <ExclamationCircleOutlined />
            </Texto>
          </Tooltip>
        )
      }

      return null
    },
  },
  {
    headerName: 'Site',
    field: 'store_number',
    minWidth: 60,
    comparator: (a, b, nodeA, nodeB) => {
      const compareSettings = {
        numeric: true,
        sensitivity: 'base',
      }

      return nodeA?.data?.store_number.localeCompare(nodeB?.data?.store_number, undefined, compareSettings)
    },
  },
  {
    headerName: 'Tank',
    field: 'tank_id',
    minWidth: 120,
    cellRenderer: ({ value }) => <BBDTag theme2>{value}</BBDTag>,
  },
  {
    headerName: 'Product',
    field: 'product',
    minWidth: 130,
  },
  {
    field: 'alerts',
    minWidth: 300,
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 80,
  },
  {
    headerName: 'Last Reading',
    valueGetter: ({ data }) => data && moment(data.updated),
    filter: 'agDateColumnFilter',
    minWidth: 80,
    valueFormatter: ({ data }) => {
      return GetDuration(data.updated).humanize()
    },
  },
  {
    headerName: 'Last Updated',
    field: 'updated',
    minWidth: 140,
    ...defaultDateDef('updated'),
  },
  {
    headerName: 'Monitor Type',
    field: 'monitor_type',
    minWidth: 60,
  },
  {
    headerName: 'Sample Rate',
    field: 'sample_rate',
    minWidth: 60,
  },
]
